import { drizzle } from "drizzle-orm/pglite";
import { PGlite } from "@electric-sql/pglite";
import { live } from "@electric-sql/pglite/live";
import { PGliteWorker } from "@electric-sql/pglite/worker";
import * as Sentry from "@sentry/react";

import { cleanupDBs } from "@/db/cleanupDBs.ts";
import { installPortalRetry } from "@/db/clientWithRetry.ts";
import { fetchUser, urlParamsFromWindowPathname } from "@/db/dbUtils.ts";
import { checkStorageAvailability, reportStorageUnavailable } from "@/db/storageCheck.ts";
import { createLogger } from "@/logger";
import { isElectron } from "@/utils";
import { QueryMonitor } from "./QueryMonitor.js";
import * as schema from "./schema.js";

const log = createLogger("storage");

const user = await fetchUser();
// Use the userId to namespace the local DB even further, in case multiple users share the same browser
const userId = user?.accounts[0]?.id ?? null;
const workspaceId = urlParamsFromWindowPathname(user?.workspaces ?? []);

// use ?dbversion=12345 to add a new db and delete
const urlDBVersion = new URLSearchParams(window?.location?.search)?.get("dbversion");
const urlHasDBVersion = !!urlDBVersion;
const localStorageDBVersion = localStorage.getItem("dbVersion");
const disableWorker = localStorage.getItem("disableWorker") === "true";
// Bumped from 2.0 → 3.0 alongside the @electric-sql/pglite 0.3.x → 0.4.x upgrade.
// PGlite 0.4.x cannot open IndexedDB databases written by 0.3.x (upstream
// electric-sql/pglite#874 — WASM aborts with `RuntimeError: unreachable`),
// so reusing the old `idb://v2.0-*` paths would force every existing user
// onto the StorageError screen. A new base version routes 0.4.x to fresh
// `idb://v3.0-*` paths; cleanupDBs() then removes the stale 2.0 DBs.
const baseVersion = "3.0";
const version = urlHasDBVersion
  ? `v${urlDBVersion}`
  : localStorageDBVersion
    ? localStorageDBVersion
    : `v${baseVersion}`;

const versionBase = `v${baseVersion}`;

if (!localStorageDBVersion) {
  try {
    localStorage.setItem("dbVersion", versionBase);
  } catch {
    // localStorage may be full; localStorageCheck() in main.tsx handles cleanup
  }
} else if (localStorageDBVersion !== versionBase) {
  try {
    localStorage.setItem("dbVersion", versionBase);
    window.location.reload();
    // reload() doesn't halt execution — prevent stale DB initialization while the page reloads
    throw new Error("DB version mismatch — reloading page");
  } catch (e) {
    if (e instanceof Error && e.message.includes("DB version mismatch")) throw e;
    // localStorage may be full — proceed with the stale version rather than reload-looping
  }
}

export const localDBVersion = `${version}-${userId}-${workspaceId}`; // Forces a user to get a new db when this changes
const USE_WORKER = !disableWorker;
export const dbName = localDBVersion ?? "no-name";
const dataDir = `idb://${dbName}`;
export const indexedDbName = `/pglite/${dbName}`;
const uniqueTabId = `tab-instance-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
const workerOptions = isElectron ? { dataDir } : { id: uniqueTabId, dataDir };

let client: PGlite;
let storageInitError: { reason: string; cause?: unknown } | null = null;

const storageAvailability = await checkStorageAvailability();
if (storageAvailability.available === false) {
  reportStorageUnavailable(storageAvailability);
  storageInitError = { reason: storageAvailability.reason };
}

// Do not open PGlite when the canary check already failed — a broken IndexedDB can
// hang PGlite init indefinitely, which blocks this module and prevents main.tsx
// from rendering StorageError.
if (!storageInitError) {
  if (USE_WORKER) {
    try {
      client = (await PGliteWorker.create(
        new Worker(new URL("./pglite-worker.js", import.meta.url), {
          type: "module",
        }),
        workerOptions,
      )) as unknown as PGlite;
    } catch (e) {
      client = {} as unknown as PGlite;
      storageInitError ??= { reason: errorReason(e), cause: e };
      log.error("[DB] Failed to initialize PGlite worker", { error: e });
      Sentry.captureException(e, { tags: { phase: "db_init", mode: "worker" } });
    }
  } else {
    try {
      client = await PGlite.create({
        debug: 0,
        relaxedDurability: true,
        extensions: { live },
        dataDir,
      });
    } catch (e) {
      client = {} as unknown as PGlite;
      storageInitError ??= { reason: errorReason(e), cause: e };
      log.error("[DB] Failed to initialize PGlite", { error: e });
      Sentry.captureException(e, { tags: { phase: "db_init", mode: "main" } });
    }
  }
} else {
  client = {} as unknown as PGlite;
}

function errorReason(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

if (!storageInitError) {
  // Install the transient `cannot drop active portal` retry on the raw PGlite client
  // before QueryMonitor wraps `query()`, so each retry is captured inside the existing
  // Postgres timing spans. See WEB-2006 / SVS-2397..2406.
  installPortalRetry(client);
  QueryMonitor.register(client);
}

const db = drizzle(client, { schema });

// Skip cleanup when storage is already known to be unavailable. cleanupDBs
// itself is now defensive against missing `indexedDB`, but there is no useful
// work to do, and we avoid emitting any noise on the StorageError path.
if (!urlHasDBVersion && !storageInitError) {
  cleanupDBs(version).then((count: number) => {
    if (count > 0) {
      console.debug(`Database cleanup complete (${count}) items removed`);
    }
  });
}

export { client, db, storageInitError, user, version, workspaceId };
